// UC CDR → Download. Build a CDR extract and take it away.
//
// Pick any combination of partner, domain, phone number, extension, direction
// and source on top of the window in the page header, see exactly how big the
// extract is and what it adds up to, then stream it out as CSV — either in the
// WebCDR export layout partners already recognise, or every field we hold.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Search, X } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, Stat, money } from "./ui";

const LAYOUTS = [
  ["webcdr", "WebCDR export", "The 23 columns partners already recognise, plus the local clock"],
  ["detail", "Full detail", "Every field we hold — trunk, carrier, rate note, session id"],
];
const DIRECTIONS = [["", "Any direction"], ["outbound", "Outbound"], ["inbound", "Inbound"],
                    ["internal", "Internal / on-net"]];
const SOURCES = [["", "Both feeds"], ["ns", "UC Platform"], ["sbc", "SBC"]];
const ANSWERED = [["", "Answered or not"], ["true", "Answered only"], ["false", "Unanswered only"]];

const BLANK = { territory: "", domain: "", number: "", extension: "", direction: "",
                source: "", answered: "", layout: "webcdr" };
const num = (n) => (n || 0).toLocaleString();

export default function DownloadTab({ window: win }) {
  const [form, setForm] = useState(BLANK);
  const [options, setOptions] = useState({ partners: [], domains: [] });
  const [domainQ, setDomainQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/uc-cdr/export/options")
      .then(({ data }) => setOptions(data))
      .catch(() => toast.error("Could not load the partner and domain lists"));
  }, []);

  const query = useCallback(() => {
    const out = { start: win?.from_at || "", end: win?.to_at || "", tz: win?.tz || "UTC" };
    ["territory", "domain", "number", "extension", "direction", "source"].forEach((k) => {
      if (form[k]) out[k] = form[k].trim();
    });
    if (form.answered) out.answered = form.answered;
    return out;
  }, [form, win]);

  const check = useCallback(async () => {
    if (!win) return;
    setBusy("check");
    try {
      const { data } = await api.get("/uc-cdr/export/preview", { params: query() });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not work out that extract");
    } finally {
      setBusy("");
    }
  }, [win, query]);

  useEffect(() => { setPreview(null); }, [form, win]);

  const download = () => {
    if (!preview?.legs) { toast.error("Nothing matches those filters yet — press Check first"); return; }
    const qs = new URLSearchParams({ ...query(), layout: form.layout }).toString();
    window.open(`${API}/uc-cdr/export/download?${qs}`, "_blank");
    toast.success(`Downloading ${num(Math.min(preview.legs, preview.max_rows))} legs`);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const domains = useMemo(() => {
    const needle = domainQ.trim().toLowerCase();
    const pool = form.territory
      ? options.domains.filter((d) => d.territory === form.territory)
      : options.domains;
    return (needle ? pool.filter((d) => d.value.toLowerCase().includes(needle)) : pool).slice(0, 40);
  }, [options.domains, domainQ, form.territory]);

  const filters = ["territory", "domain", "number", "extension", "direction", "source", "answered"]
    .filter((k) => form[k]);

  return (
    <div className="space-y-4" data-testid="uc-cdr-download">
      <p className="max-w-3xl text-sm text-slate-500">
        The period comes from the date range in the page header, so set that first. Everything
        below narrows it further — leave a field alone to include everything.
      </p>

      <Card className="grid gap-4 px-4 py-4 lg:grid-cols-3" data-testid="uc-cdr-download-form">
        <div>
          <Label className="text-xs text-slate-500">Partner (territory)</Label>
          <select value={form.territory} onChange={(e) => { set("territory")(e); setForm((f) => ({ ...f, domain: "" })); }}
                  data-testid="uc-cdr-download-territory"
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">Every partner</option>
            {options.partners.map((p) => (
              <option key={p.value} value={p.value}>
                {`${p.label === p.value ? p.value : `${p.label} · ${p.value}`} (${num(p.domains)})`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-xs text-slate-500">Domain (customer)</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-slate-400" />
            <Input className="pl-8" placeholder={form.domain || "Type to find a domain"}
                   value={domainQ} onChange={(e) => setDomainQ(e.target.value)}
                   data-testid="uc-cdr-download-domain-search" />
            {form.domain && (
              <button onClick={() => { setForm((f) => ({ ...f, domain: "" })); setDomainQ(""); }}
                      data-testid="uc-cdr-download-domain-clear"
                      className="absolute right-2 top-3 text-slate-400 hover:text-slate-700">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {form.domain
            ? <div className="mt-1 text-[12px] font-medium text-slate-700">{form.domain}</div>
            : !!domainQ && (
              <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200"
                   data-testid="uc-cdr-download-domain-list">
                {domains.map((d) => (
                  <button key={d.value} onClick={() => { setForm((f) => ({ ...f, domain: d.value })); setDomainQ(""); }}
                          data-testid={`uc-cdr-download-domain-${d.value}`}
                          className="flex w-full items-center justify-between px-2 py-1 text-left text-[12px] hover:bg-slate-50">
                    <span className="text-slate-700">{d.value}</span>
                    <span className="text-slate-400">{num(d.calls)}</span>
                  </button>
                ))}
                {!domains.length && <div className="px-2 py-1 text-[12px] text-slate-400">No match</div>}
              </div>
            )}
        </div>

        <div>
          <Label className="text-xs text-slate-500">Phone number (called or calling)</Label>
          <Input value={form.number} onChange={set("number")} placeholder="e.g. 587 207 5795"
                 data-testid="uc-cdr-download-number" />
        </div>

        <div>
          <Label className="text-xs text-slate-500">Extension</Label>
          <Input value={form.extension} onChange={set("extension")} placeholder="e.g. 2001"
                 data-testid="uc-cdr-download-extension" />
        </div>

        <div>
          <Label className="text-xs text-slate-500">Direction</Label>
          <select value={form.direction} onChange={set("direction")} data-testid="uc-cdr-download-direction"
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            {DIRECTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </div>

        <div>
          <Label className="text-xs text-slate-500">Feed</Label>
          <select value={form.source} onChange={set("source")} data-testid="uc-cdr-download-source"
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            {SOURCES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </div>

        <div>
          <Label className="text-xs text-slate-500">Answered</Label>
          <select value={form.answered} onChange={set("answered")} data-testid="uc-cdr-download-answered"
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            {ANSWERED.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </div>

        <div className="lg:col-span-2">
          <Label className="text-xs text-slate-500">Layout</Label>
          <div className="flex flex-wrap gap-2">
            {LAYOUTS.map(([value, label, hint]) => (
              <button key={value} onClick={() => setForm((f) => ({ ...f, layout: value }))}
                      data-testid={`uc-cdr-download-layout-${value}`}
                      className={`flex-1 rounded-md border px-3 py-2 text-left transition-colors ${
                        form.layout === value
                          ? "border-slate-800 bg-slate-800 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                <div className="text-[13px] font-semibold">{label}</div>
                <div className={`text-[11px] ${form.layout === value ? "text-slate-300" : "text-slate-400"}`}>{hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:col-span-3">
          <Button size="sm" onClick={check} disabled={!!busy || !win} data-testid="uc-cdr-download-check">
            {busy === "check" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Check
          </Button>
          <Button size="sm" variant="outline" onClick={download} disabled={!preview?.legs}
                  data-testid="uc-cdr-download-go">
            <Download className="h-4 w-4" /> Download CSV
          </Button>
          {!!filters.length && (
            <Button size="sm" variant="ghost" onClick={() => setForm({ ...BLANK, layout: form.layout })}
                    data-testid="uc-cdr-download-clear">Clear filters</Button>
          )}
          <span className="text-[12px] text-slate-500" data-testid="uc-cdr-download-window">
            {win?.label || "…"}
          </span>
        </div>
      </Card>

      {preview && (
        <div className="space-y-3" data-testid="uc-cdr-download-preview">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Call legs" value={num(preview.legs)}
                  tone={preview.legs ? "text-slate-800" : "text-slate-400"}
                  hint={preview.capped ? `capped at ${num(preview.max_rows)} rows` : ""}
                  testid="uc-cdr-download-stat-legs" />
            {preview.totals.map((t) => (
              <Stat key={t.currency || "none"} testid={`uc-cdr-download-stat-${t.currency || "unpriced"}`}
                    label={t.currency ? `Priced in ${t.currency}` : "Not priced"}
                    value={t.currency ? money(t.amount, t.currency) : num(t.legs)}
                    hint={`${Math.round(t.minutes).toLocaleString()} min · ${Math.round(t.billed_minutes).toLocaleString()} billed`} />
            ))}
            {preview.totals_skipped && (
              <Stat label="Totals" value="too many legs" tone="text-slate-400"
                    hint={`narrow below ${num(preview.totals_limit)} legs to see minutes and spend`}
                    testid="uc-cdr-download-stat-skipped" />
            )}
          </div>

          {!preview.legs ? (
            <Card className="px-4 py-8 text-center text-sm text-slate-500" data-testid="uc-cdr-download-empty">
              Nothing matches those filters in this window. Widen the date range in the header, or
              clear a filter.
            </Card>
          ) : (
            <Card className="overflow-x-auto">
              <div className="border-b border-slate-100 px-4 py-2 text-[12px] text-slate-500">
                The newest {preview.sample.length} of {num(preview.legs)} legs — the file itself runs oldest first
              </div>
              <table className="w-full text-left text-[13px]" data-testid="uc-cdr-download-sample">
                <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-1.5">Started (UTC)</th>
                    <th className="px-3 py-1.5">Domain</th>
                    <th className="px-3 py-1.5">Dir</th>
                    <th className="px-3 py-1.5">From</th>
                    <th className="px-3 py-1.5">To</th>
                    <th className="px-3 py-1.5 text-right">Secs</th>
                    <th className="px-3 py-1.5">Trunk</th>
                    <th className="px-3 py-1.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono text-[12px] text-slate-600">
                        {(r.started_at || "").replace("T", " ").replace("Z", "")}
                      </td>
                      <td className="px-3 py-1.5 text-slate-700">{r.domain || <span className="text-amber-600">no domain</span>}</td>
                      <td className="px-3 py-1.5"><Pill>{r.direction || "—"}</Pill></td>
                      <td className="px-3 py-1.5 font-mono text-[12px] text-slate-600">{r.ani || "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-[12px] text-slate-600">{r.dialed || "—"}</td>
                      <td className="px-3 py-1.5 text-right text-slate-600">{num(r.duration_seconds)}</td>
                      <td className="px-3 py-1.5 text-[12px] text-slate-500">{r.carrier || r.source}</td>
                      <td className="px-3 py-1.5 text-right text-slate-700">
                        {r.amount ? money(r.amount, r.currency) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
